/* eslint-disable react-hooks/exhaustive-deps */
import {
  BookText,
  Calendar,
  ChevronRight,
  FileText,
  Mail,
  Phone,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  fetchAdminData,
  fetchBankLeadData,
  fetchOfficeData,
  fetchSupplierData,
  updateFirestoreDoc,
} from "../../../api";
import { Oppsummering } from "./oppsummering";
import { Fremdriftsplan } from "./Fremdriftsplan";
import { Documenters } from "./document";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../../config/firebaseConfig";
import { formatNorwegianPhone2 } from "../../../lib/utils";
import { useAuthData } from "../../../context/AuthContext";

export const LeadsDetails = () => {
  const location = useLocation();
  const pathSegments = location.pathname.split("/");
  const id = pathSegments.length > 2 ? pathSegments[2] : null;
  const [bankData, setBankData] = useState<any>();
  const { adminData } = useAuthData();

  const [role, setRole] = useState<string | null>(null);

  useEffect(() => {
    const getData = async () => {
      if (adminData?.role) {
        setRole(adminData.role);
        setLoginUser(adminData);
      }
    };

    getData();
  }, [adminData]);

  const getData = useCallback(async () => {
    if (!id) return;

    try {
      const data = await fetchBankLeadData(id);
      if (data) {
        setBankData(data);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  }, [id]);

  useEffect(() => {
    if (!id) {
      return;
    }

    getData();
  }, [getData]);

  const [activeTab, setActiveTab] = useState<any>(0);
  const tabData = [
    { label: "Summary", icon: <BookText className="w-5 h-5 md:w-6 md:h-6" /> },
    {
      label: "Dokumentasjon",
      icon: <FileText className="w-5 h-5 md:w-6 md:h-6" />,
    },
    {
      label: "Fremdriftsplan",
      icon: <Calendar className="w-5 h-5 md:w-6 md:h-6" />,
    },
  ];
  useEffect(() => {
    const timeout = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);

    return () => clearTimeout(timeout);
  }, [activeTab]);

  const [assignUser, setAssignUser] = useState<any>(null);

  useEffect(() => {
    const getData = async () => {
      const data: any = await fetchAdminData(bankData?.assignedTo);
      if (data) {
        setAssignUser(data);
      }
    };
    if (bankData && bankData?.assignedTo) {
      getData();
    }
  }, [bankData]);

  const [users, setUsers] = useState([]);

  const fetchUsersData = async () => {
    try {
      const q = query(
        collection(db, "admin"),
        where("role", "==", "Bankansvarlig")
      );

      const querySnapshot = await getDocs(q);

      const data: any = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setUsers(data);
    } catch (error) {
      console.error("Error fetching husmodell data:", error);
    }
  };

  useEffect(() => {
    fetchUsersData();
  }, []);

  const [loginUser, setLoginUser] = useState<any>(null);

  const [leadCreate, setLeadCreate] = useState<any>(null);
  const [supplierData, setSupplierData] = useState<any>(null);
  const [officeData, setOfficeData] = useState<any>(null);

  useEffect(() => {
    if (!bankData || !bankData?.created_by) return;

    const getData = async () => {
      const data = await fetchAdminData(bankData?.created_by);
      if (data) {
        setLeadCreate(data);

        const promises: Promise<any>[] = [];

        if (data?.supplier) {
          promises.push(fetchSupplierData(data?.supplier));
        }
        if (data?.office) {
          promises.push(fetchOfficeData(data?.office));
        }

        if (promises.length > 0) {
          const [supplierData, officeData] = await Promise.all([
            data?.supplier ? fetchSupplierData(data?.supplier) : null,
            data?.office ? fetchOfficeData(data?.office) : null,
          ]);

          if (supplierData) setSupplierData(supplierData);
          if (officeData) setOfficeData(officeData);
        }
      }
    };

    getData();
  }, [bankData]);

  const tabRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = tabRef.current;
    if (!container) return;

    const activeButton = container.querySelectorAll("button")[activeTab];
    if (activeButton) {
      const containerRect = container.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      const scrollLeft =
        activeButton.offsetLeft -
        containerRect.width / 2 +
        buttonRect.width / 2;

      container.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }
  }, [activeTab]);

  return (
    <>
      <div className="p-2 pt-0">
        <div className="border border-gray3 rounded-lg">
          <div className="p-4 flex flex-col gap-3 md:gap-5 border-b border-gray3">
            <div className="flex items-center gap-1 flex-wrap">
              <Link
                to={`${
                  role === "Bankansvarlig" ? "/bank-leads" : "/agent-leads"
                }`}
                className="text-gray4 text-xs md:text-sm cursor-pointer"
              >
                Bank Leads
              </Link>
              <ChevronRight className="h-4 w-4 text-gray5" />
              <span className="text-black3 text-sm md:text-base lg:text-lg font-bold">
                Lead Details
              </span>
            </div>
            <div className="flex items-center gap-2 justify-between">
              <div className="flex flex-col gap-3 md:gap-5">
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                    <div className="text-darkBlack text-lg md:text-xl desktop:text-2xl font-semibold">
                      {bankData?.Kunden?.Kundeinformasjon[0]?.f_name}{" "}
                      {bankData?.Kunden?.Kundeinformasjon[0]?.l_name}{" "}
                    </div>
                  </div>
                  <div className="flex md:items-center gap-2 sm:gap-4 md:gap-6 flex-col md:flex-row">
                    <p className="text-black3 text-xs md:text-sm font-medium">
                      {bankData?.plotHusmodell?.plot?.address}
                    </p>
                    <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-gray5" />
                        <span className="text-gray4 text-xs md:text-sm">
                          {bankData?.Kunden?.Kundeinformasjon[0]?.EPost}
                        </span>
                      </div>
                      <div className="h-3 border-l border-grey"></div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-5 h-5 text-gray5" />
                        <span className="text-gray4 text-xs md:text-sm">
                          {formatNorwegianPhone2(
                            bankData?.Kunden?.Kundeinformasjon[0]?.mobileNummer
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {assignUser && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-3">
                    <span className="text-darkBlack text-xs md:text-sm font-semibold flex items-center gap-2">
                      Bank Agent:{" "}
                      <span className="text-black3 font-medium">
                        {assignUser?.f_name ?? assignUser?.name}{" "}
                        {assignUser?.l_name ?? ""}
                      </span>
                    </span>{" "}
                    <div className="h-3 border-l border-grey hidden sm:block"></div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-5 h-5 text-gray5" />
                      <span className="text-gray4 text-xs md:text-sm">
                        {assignUser?.email}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {leadCreate && (
                <div className="bg-[#E3FFFC] rounded-[4px] py-3 px-4 lg:max-w-[400px]">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="h-10 sm:h-12 w-10 sm:w-12">
                      <img
                        src={leadCreate?.photo}
                        alt="avatar"
                        className="w-full h-full rounded-full"
                      />
                    </div>
                    <div className="truncate flex-1">
                      <h4 className="text-black3 text-xs md:text-sm font-semibold mb-1 truncate">
                        {leadCreate?.f_name
                          ? `${leadCreate?.f_name} ${leadCreate?.l_name}`
                          : leadCreate?.name}
                      </h4>
                      <div className="flex items-center gap-2 truncate">
                        <Mail className="w-5 h-5 text-gray5" />
                        <span className="text-gray4 text-xs md:text-sm">
                          {leadCreate?.email}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 md:gap-3 mt-2">
                    <p className="text-gray4 text-xs md:text-sm">
                      {supplierData?.company_name}
                    </p>
                    {officeData && officeData?.data?.name && (
                      <>
                        <div className="h-3 border-l border-grey"></div>
                        <p className="text-gray4 text-xs md:text-sm">
                          {officeData?.data?.name}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="relative flex flex-col lg:flex-row items-start gap-6 px-3 lg:px-4 py-4 lg:py-5">
             <div className="w-full lg:w-[300px] HideScrollbar flex flex-col gap-4">
              <div ref={tabRef} className="overflow-x-auto HideScrollbar">
                <div className="flex lg:flex-col gap-2 bg-white">
                  {tabData.map((tab, index) => (
                    <button
                      key={index}
                      className={`text-sm whitespace-nowrap relative ${
                        id ? "cursor-pointer" : "cursor-auto"
                      } flex items-center gap-2 text-darkBlack py-2 px-2 md:px-3 rounded-lg ${
                        activeTab === index
                          ? "font-semibold bg-darkGreen2 text-primary"
                          : "text-gray5"
                      }`}
                      onClick={() => setActiveTab(index)}
                      style={{
                        boxShadow:
                          activeTab === index
                            ? "0px 1px 2px 0px #1018281A"
                            : "",
                      }}
                    >
                      {activeTab === index && (
                        <div
                          className="absolute left-1 border-l-2 border-green3 h-5"
                          style={{
                            boxShadow: "3px 0 24px 4px rgba(0, 136, 70, 0.6)",
                            backdropFilter: "blur(8px)",
                            borderRadius: "2px",
                          }}
                        ></div>
                      )}
                      <span
                        className={`${activeTab === index ? "text-white" : ""}`}
                      >
                        {tab.icon}
                      </span>
                      <span
                        className={`${
                          activeTab === index ? "text-white" : "text-gray4"
                        }`}
                      >
                        {tab.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="w-full">
                <p className={`text-black mb-[6px] text-sm font-medium`}>
                  Tildel til
                </p>
                <Select
                  onValueChange={async (value) => {
                    try {
                      if (!id) return;

                      const formatDate = (date: Date) => {
                        return date
                          .toLocaleString("sv-SE", { timeZone: "UTC" })
                          .replace(",", "");
                      };

                      await updateFirestoreDoc(db, "bank_leads", id, {
                        assignedTo: value,
                        updatedAt: formatDate(new Date()),
                        assign: true,
                      });
                      setBankData((prev: any) => ({
                        ...prev,
                        assignedTo: value,
                      }));
                    } catch (error) {
                      console.error("Error updating assignment:", error);
                    }
                  }}
                  value={bankData?.assignedTo ?? ""}
                >
                  <SelectTrigger
                    className={`bg-white rounded-[8px] border text-black border-gray1`}
                  >
                    <SelectValue placeholder="Velg tilordne til" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectGroup>
                      <SelectItem value={loginUser?.id}>
                        Tildel til meg
                      </SelectItem>
                      {users
                        ?.filter((user: any) => user?.id !== loginUser?.id)
                        .map((user: any, index: number) => (
                          <SelectItem value={user?.id} key={index}>
                            {!user?.f_name
                              ? user?.name
                              : `${user?.f_name} ${user?.l_name}`}
                          </SelectItem>
                        ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
             <div className="w-full lg:w-[calc(100%-300px)]">
              {activeTab === 0 && (
                <Oppsummering bankData={bankData} setActiveTab={setActiveTab} />
              )}
              {activeTab === 1 && (
                <Documenters getData={getData} setActiveTab={setActiveTab} />
              )}
              {activeTab === 2 && (
                <Fremdriftsplan bankData={bankData} getData={getData} />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
